import { Button } from "@dash/form";
import { useToast } from "@dash/toast";
import { useEffect, useState } from "react";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import { useQueryClient } from "react-query";

import { ProductAvailabiliyStatus } from "@megaron/crm-contracts";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { newUuid, Uuid } from "@megaron/uuid";

type Props = {
  customerUuid: Uuid;
  queryKey: string | string[];
  showDate?: boolean;
};

type ItemOption = {
  label: string;
  value: string;
};

type AvailabilityData = {
  available: boolean;
  date?: string;
};

export const SaveProductAvailability: React.FC<Props> = ({ customerUuid, queryKey, showDate = true }) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [items, setItems] = useState<ItemOption[]>([]);
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, AvailabilityData>>({});

  const addProductAvailabilityMutation = useClientManager("crm").addProductAvailability().useMutation();
  const customerAvailabilityQuery = useClientManager("crm")
    .customerProductsAvailabilityQuery()
    .useQuery({ customerId: customerUuid });

  const docs = useServiceClient("docs");

  useEffect(() => {
    const fetchItems = async () => {
      const results = await docs.searchItems({ searchText: "" });
      if (!results?.value?.items) return;
      const filteredItems = results.value.items.filter((i) => i.isInAvailability === true);
      const order = [
        "A-2_1,5",
        "A-2_5",
        "A-2_20",
        "A-6_10",
        "C-50_1,5",
        "C-50_5",
        "C-50_10",
        "C-50_17",
        "C-50s_5",
        "S-8_20",
        "A-11_17",
        "Dv-21_17",
        "Dv-20_1,5",
        "Dv-20_5",
        "Dv-20_20",
        "Df-16_20",
        "F-60_5",
        "F-60_15",
        "F-60_18",
        "F-2_5",
        "F-2_15",
        "Dw-13_5",
        "Dw-13_15",
        "Gt-120_10",
        "Gt-120_20",
        "Gs-1_10",
        "Gs-1_20",
        "Gv-15_20",
        "Gs-2_20",
        "Gs-3_20",
        "Gl-5_20",
        "Gl-3_20",
        "Uv-903_1",
        "Uv-811_1",
        "Uv-715_1",
        "Uv-213_1",
        "Uv-903_3",
        "Uv-811_3",
        "Uv-715_3",
        "Uv-213_3",
        "MR-1_17",
        "SMT-1_1,5",
        "SMT-1_5",
        "SMT-1_17",
      ];
      const normalizeName = (name: string) => name.replace(/\/\d+/g, "").trim();
      const inOrderItems = order
        .map((name) => filteredItems.find((item) => normalizeName(item.itemName) === normalizeName(name)))
        .filter((item): item is (typeof filteredItems)[number] => !!item);
      const leftoverItems = filteredItems.filter(
        (item) => !order.some((name) => normalizeName(item.itemName) === normalizeName(name)),
      );
      const sortedItems = [...inOrderItems, ...leftoverItems];
      const options: ItemOption[] = sortedItems.map((item) => ({
        label: item.itemName ?? item.itemId,
        value: item.itemId,
      }));

      setItems(options);
      const availabilityData = customerAvailabilityQuery.data ?? [];
      const initialMap: Record<string, AvailabilityData> = {};
      for (const item of options) {
        const match = availabilityData.find((a) => a.itemId === item.value);
        initialMap[item.value] = {
          available: match?.status === "available",
          date: match?.date ? new Date(match.date).toISOString() : undefined,
        };
      }
      setAvailabilityMap(initialMap);
    };
    fetchItems();
  }, [customerAvailabilityQuery.data]);

  const updateAvailability = (itemId: string, value: boolean) => {
    const status: ProductAvailabiliyStatus = value ? "available" : "unavailable";
    const nowISOString = new Date().toISOString();

    setAvailabilityMap((prev) => ({
      ...prev,
      [itemId]: {
        available: value,
        date: nowISOString,
      },
    }));

    addProductAvailabilityMutation.mutate(
      {
        id: newUuid(),
        itemId,
        posId: customerUuid,
        status,
        date: new Date(),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(queryKey);
        },
        onError: () => {
          toast.error(`Błąd podczas zapisywania dostępności produktu`);
        },
      },
    );
  };

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "18px",
        overflowY: "auto",
        maxHeight: "75dvh",
        padding: "2px",
        justifyContent: "center",
      }}
    >
      <Button
        onClick={() => {
          const now = new Date();
          const nowISOString = now.toISOString();

          setAvailabilityMap((prev) => {
            const updated: typeof prev = {};
            for (const [itemId, data] of Object.entries(prev)) {
              if (typeof data.available === "boolean" && data.date) {
                updated[itemId] = {
                  ...data,
                  date: nowISOString,
                };
              } else {
                updated[itemId] = data;
              }
            }
            return updated;
          });

          Object.entries(availabilityMap).forEach(([itemId, data]) => {
            if (typeof data.available === "boolean" && data.date) {
              const status: ProductAvailabiliyStatus = data.available ? "available" : "unavailable";
              addProductAvailabilityMutation.mutate({
                id: newUuid(),
                itemId,
                posId: customerUuid,
                status,
                date: now,
              });
            }
          });
        }}
      >
        Zatwierdź wszystko ponownie
      </Button>

      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          borderRadius: "8px",
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {items.map((item) => {
          const availability = availabilityMap[item.value] ?? { available: false };
          const { available, date } = availability;

          const handleSetAvailable = (value: boolean) => updateAvailability(item.value, value);

          return (
            <div
              key={item.value}
              css={{
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "#ccc",
                borderRadius: "8px",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <strong>{item.label}</strong>
                  {showDate && (
                    <div css={{ fontSize: "12px", color: "#555" }}>
                      Ostatnio: {date ? new Date(date).toLocaleString("pl-PL") : <em>brak uzupełnienia</em>}
                    </div>
                  )}
                </div>
                <span
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "14px",
                    color: "#000",
                  }}
                >
                  {available ? (
                    <>
                      <span>Dostępny</span>
                      <MdCheckCircle size={18} color="#38A169" />
                    </>
                  ) : (
                    <>
                      <span>Niedostępny</span>
                      <MdCancel size={18} color="#E53E3E" />
                    </>
                  )}
                </span>
              </div>
              <div
                css={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                }}
              >
                <Button
                  onClick={() => handleSetAvailable(true)}
                  css={{
                    flexGrow: 1,
                    minWidth: 120,
                  }}
                >
                  Dostępny
                </Button>
                <Button
                  onClick={() => handleSetAvailable(false)}
                  css={{
                    flexGrow: 1,
                    minWidth: 120,
                  }}
                >
                  Niedostępny
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
